import React, { useState, useEffect, useCallback, useMemo } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { PencilEdit02Icon } from "@hugeicons/core-free-icons";
import { Button, Modal } from "react-bootstrap";

export default function OtpModal({
  show,
  onHide,
  title,
  helpText,
  prevLabel,
  nextLabel,
  form,
  setForm,
  onGetOtp,
  onSubmit,
}) {
  const [error, setError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [countdown, setCountdown] = useState(0);

  const COOLDOWN = 30;

  const isEmailMode = /(e[-_\s]?mail)/i.test(nextLabel || "");
  const isMobileMode = /(mobile|number|phone)/i.test(nextLabel || "");

  const emailRegex = useMemo(
    () => /^(?=.{1,254}$)(?=.{1,64}@)[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
    []
  );

  const isNextValid = useMemo(() => {
    const val = (form.next || "").trim();
    if (!val) return false;
    if (isMobileMode) return /^[6-9]\d{9}$/.test(val);
    if (isEmailMode) return emailRegex.test(val);
    return true;
  }, [form.next, isEmailMode, isMobileMode, emailRegex]);

  const validate = useCallback(
    (value) => {
      if (!value) {
        setError(`${nextLabel} is required`);
        return false;
      }
      if (isMobileMode) {
        if (!/^[6-9]\d{9}$/.test(value)) {
          setError("Enter valid 10 digit mobile number starting with 6-9");
          return false;
        }
      }
      if (isEmailMode) {
        if (!emailRegex.test(value)) {
          setError("Enter valid email address");
          return false;
        }
      }
      setError("");
      return true;
    },
    [isEmailMode, isMobileMode, nextLabel, emailRegex]
  );

  // Countdown effect
  useEffect(() => {
    if (countdown <= 0) return;
    const id = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [countdown]);

  // Reset on close
  useEffect(() => {
    if (!show) {
      setCountdown(0);
      setError("");
      setOtpError("");
    }
  }, [show]);

  const handleGetOtp = () => {
    if (!validate(form.next)) return;
    onGetOtp && onGetOtp();
    setCountdown(COOLDOWN);
  };

  const handleSubmit = (e) => {
    if (!validate(form.next)) {
      e.preventDefault();
      return;
    }
    if (!form.otp || !form.otp.trim()) {
      e.preventDefault();
      setOtpError("OTP is required");
      return;
    }
    onSubmit && onSubmit(e);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="otp-edit-modal"
    >
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton className="border-0 pb-0">
          <div className="d-flex align-items-start gap-3">
            <div className="otp-modal-icon-wrapper">
              <HugeiconsIcon
                icon={PencilEdit02Icon}
                size={30}
                strokeWidth={1.6}
              />
            </div>
            <div className="pt-1">
              <div className="modal-title mb-1">{title}</div>
              {helpText && (
                <div
                  className="text-muted"
                  style={{ fontSize: "0.875rem", fontWeight: 400 }}
                >
                  {helpText}
                </div>
              )}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <div className="mb-3">
            <label className="form-label">{prevLabel}</label>
            <input className="form-control" value={form.prev} readOnly />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {nextLabel} <span className="text-danger">*</span>
            </label>
            <div className="d-flex gap-2 flex-column">
              <input
                type={isMobileMode ? "number" : "text"}
                className={`form-control ${error ? "is-invalid" : ""}`}
                value={form.next}
                onChange={(e) => {
                  let v = e.target.value;
                  if (isMobileMode) v = v.replace(/\D/g, "");
                  v = v.trimStart();
                  setForm((m) => ({ ...m, next: v }));
                  validate(v.trim());
                }}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  setForm((m) => ({ ...m, next: v }));
                  validate(v);
                }}

                placeholder={`Enter new ${nextLabel.toLowerCase()}`}
                inputMode={isMobileMode ? "numeric" : undefined}
                autoComplete={
                  isEmailMode ? "email" : isMobileMode ? "tel" : undefined
                }
              />
              {error && <div className="invalid-feedback d-block">{error}</div>}
            </div>
            <div className="d-flex justify-content-end mt-3">
              <Button
                type="button"
                onClick={handleGetOtp}
                disabled={countdown > 0 || !!error || !isNextValid}
                className="otp-badge-btn"
              >
                <HugeiconsIcon
                  icon={PencilEdit02Icon}
                  size={18}
                  color="currentColor"
                  strokeWidth={1.5}
                />
                {countdown > 0
                  ? `Resend in ${countdown}s`
                  : form.otpSent
                    ? "Resend OTP"
                    : "Get OTP"}
              </Button>
            </div>
          </div>
          <div className="mb-1">
            {/* <label className="form-label">
              Enter OTP <span className="text-danger">*</span>
            </label> */}
            <input
              type="number"
              maxLength={6}
              className="form-control"
              value={form.otp}
              onChange={(e) => {
                setForm((m) => ({ ...m, otp: e.target.value.trim() }));
                if (otpError) setOtpError("");
              }}
              placeholder={form.otpSent ? "Enter OTP" : "Request OTP first"}
              disabled={!form.otpSent}
            />
            {otpError && (
              <div className="text-danger small mt-1">{otpError}</div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={onHide}>
            No, Cancel
          </Button>
          <Button variant="primary" type="submit">
            Update
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
